import BaseRealizedPresetEffect from './base_realized_preset_effect.js';
import { TIMEOUTS } from './shared/constants.js';

/**
 * NewRealizedPresetNodeImageEffect
 * 
 * Handles dynamic image display for inventorius selections in the realized preset form.
 * Uses custom CSS classes as hooks for DOM manipulation.
 */
class NewRealizedPresetNodeImageEffect extends BaseRealizedPresetEffect {
  constructor() {
    super();
    this.initialize();
  }

  /**
   * Initialize the image effect functionality
   */
  initialize() {
    this.initializeEventListeners();
    this.loadPreselectedImages();
  }

  /**
   * Initialize event listeners for all preset node containers
   */
  initializeEventListeners() {
    const nodeContainers = document.querySelectorAll('.js-preset-node-container');
    
    nodeContainers.forEach(container => {
      const nodeType = container.dataset.nodeType;
      const nodeIndex = container.dataset.nodeIndex;
      
      if (nodeType === 'designation') {
        this.setupDesignationListeners(container, nodeIndex);
      } else if (nodeType === 'item_model') {
        this.setupItemModelListeners(container, nodeIndex);
      }
    });
  }

  /**
   * Setup event listeners for designation nodes (item_model + color selection)
   */
  setupDesignationListeners(container, nodeIndex) {
    const itemModelSelect = container.querySelector('.js-item-model-select');
    const colorSelect = container.querySelector('.js-color-select');
    
    if (itemModelSelect && colorSelect) {
      const handleChange = () => {
        this.debounceDesignationChange(container, nodeIndex, itemModelSelect, colorSelect);
      };
      
      itemModelSelect.addEventListener('change', handleChange);
      colorSelect.addEventListener('change', handleChange);
    }
  }

  /**
   * Setup event listeners for item_model nodes (direct inventorius selection)
   */
  setupItemModelListeners(container, nodeIndex) {
    const inventoriusSelect = container.querySelector('.js-inventorius-select');
    
    if (inventoriusSelect) {
      inventoriusSelect.addEventListener('change', () => {
        this.debounceInventoriusChange(container, nodeIndex, inventoriusSelect);
      });
    }
  }

  /**
   * Debounced handler for designation changes
   */
  debounceDesignationChange(container, nodeIndex, itemModelSelect, colorSelect) {
    this.debounce(() => {
      this.handleDesignationChange(container, nodeIndex, itemModelSelect, colorSelect);
    }, 300, `designation_${nodeIndex}`);
  }

  /**
   * Debounced handler for inventorius changes
   */
  debounceInventoriusChange(container, nodeIndex, inventoriusSelect) {
    this.debounce(() => {
      this.handleInventoriusChange(container, nodeIndex, inventoriusSelect);
    }, 300, `inventorius_${nodeIndex}`);
  }

  /**
   * Handle designation node changes (item_model + color)
   */
  handleDesignationChange(container, nodeIndex, itemModelSelect, colorSelect) {
    const itemModelId = itemModelSelect.value;
    const colorId = colorSelect.value;
    
    // Only proceed if BOTH selections are made
    if (!itemModelId || !colorId) {
      // Don't show any loading or animation states - just maintain placeholder
      return;
    }
    
    const cacheKey = `designation_${itemModelId}_${colorId}`;
    
    if (this.cache.has(cacheKey)) {
      this.updateImageDisplay(container, this.cache.get(cacheKey));
    } else {
      this.fetchInventoriusData({
        item_model_id: itemModelId,
        color_id: colorId
      }, container, nodeIndex, cacheKey);
    }
  }

  /**
   * Handle item_model node changes (direct inventorius selection)
   */
  handleInventoriusChange(container, nodeIndex, inventoriusSelect) {
    const inventoriusId = inventoriusSelect.value;
    
    if (!inventoriusId) {
      this.clearImageDisplay(container);
      return;
    }
    
    const cacheKey = `inventorius_${inventoriusId}`;
    
    if (this.cache.has(cacheKey)) {
      this.updateImageDisplay(container, this.cache.get(cacheKey));
    } else {
      this.fetchInventoriusData({
        inventorius_id: inventoriusId
      }, container, nodeIndex, cacheKey);
    }
  }

  /**
   * Fetch inventorius data from the server
   */
  async fetchInventoriusData(params, container, nodeIndex, cacheKey) {
    try {
			this.showImageLoadingState(container);

			// sleep to mitigate image error race condition
			// https://stackoverflow.com/a/39914235/31392702
			await new Promise(r => setTimeout(r, TIMEOUTS.IMAGE_LOADING_SLEEP));				
      
      const data = await this.fetchData(
        '/realized_presets/fetch_inventorius_image',
        params,
        cacheKey,
        nodeIndex
      );
      
      this.updateImageDisplay(container, data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching inventorius data:', error);
        this.showImageErrorState(container);
      }
    }
  }

  /**
   * Update the image display with inventorius data
   */
  updateImageDisplay(container, data) {
    const imageDisplay = container.querySelector('.js-inventorius-image-display');
    if (!imageDisplay) return;
    
    const inventorius = data.inventorius;
    
    if (inventorius.has_image && inventorius.image_url) {
      imageDisplay.innerHTML = `
        <div class="inventorius-image-container">
          <img src="${inventorius.image_url}" 
               alt="${inventorius.name}" 
               class="js-inventorius-image inventorius-image"
               loading="lazy">
          <div class="inventorius-image-caption">
            ${inventorius.name}
          </div>
        </div>
      `;
    } else {
      this.showPlaceholder(container, inventorius.name);
    }
  }

  /**
   * Show loading state for image display
   */
  showImageLoadingState(container) {
    const imageDisplay = container.querySelector('.js-inventorius-image-display');
    if (!imageDisplay) return;
    
    imageDisplay.innerHTML = `
      <div class="inventorius-image-placeholder loading">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <div class="placeholder-text">Loading image...</div>
      </div>
    `;
  }

  /**
   * Show error state for image display
   */
  showImageErrorState(container) {
    const imageDisplay = container.querySelector('.js-inventorius-image-display');
    if (!imageDisplay) return;
    
    imageDisplay.innerHTML = `
      <div class="inventorius-image-placeholder error">
        <svg width="48" height="48" fill="currentColor" class="text-danger" viewBox="0 0 16 16">
          <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
        </svg>
        <div class="placeholder-text">Error loading image</div>
      </div>
    `;
  }

  /**
   * Show placeholder when no image is available
   */
  showPlaceholder(container, itemName = '') {
    const imageDisplay = container.querySelector('.js-inventorius-image-display');
    if (!imageDisplay) return;
    
    imageDisplay.innerHTML = `
      <div class="inventorius-image-placeholder">
        <svg width="48" height="48" fill="currentColor" class="text-muted" viewBox="0 0 16 16">
          <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
          <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z"/>
        </svg>
        <div class="placeholder-text">
          ${itemName ? itemName : 'No image available'}
        </div>
      </div>
    `;
  }

  /**
   * Clear image display
   */
  clearImageDisplay(container) {
    const imageDisplay = container.querySelector('.js-inventorius-image-display');
    if (!imageDisplay) return;
    
    imageDisplay.innerHTML = `
      <div class="inventorius-image-placeholder">
        <svg width="48" height="48" fill="currentColor" class="text-muted" viewBox="0 0 16 16">
          <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
          <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z"/>
        </svg>
        <div class="placeholder-text">Select an option</div>
      </div>
    `;
  }

  /**
   * Load images for pre-selected inventorius nodes
   */
  loadPreselectedImages() {
    const preselectedContainers = document.querySelectorAll('.js-preset-node-container[data-node-type="inventorius"]');
    
    preselectedContainers.forEach(container => {
      const nodeIndex = container.dataset.nodeIndex;
      const hiddenInput = container.querySelector(`input[name*="selection_${nodeIndex}"]`);
      
      if (hiddenInput && hiddenInput.value) {
        const inventoriusId = hiddenInput.value;
        const cacheKey = `inventorius_${inventoriusId}`;
        
        if (this.cache.has(cacheKey)) {
          this.updateImageDisplay(container, this.cache.get(cacheKey));
        } else {
          this.fetchInventoriusData({
            inventorius_id: inventoriusId
          }, container, nodeIndex, cacheKey);
        }
      }
    });
  }
}

export default NewRealizedPresetNodeImageEffect;
